import { useContext } from 'react';
import { RJSFSchema } from '@rjsf/utils';
import styled from '@emotion/styled';
import { Box, Divider, Grid, Typography } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { convertFromSnakeCase, getLineageMetaTitle, titleCase } from '@/_utils/utils';
import { BoolIcon, LabelValue } from '@/exports/columnCommonComponents';
import MediaSupportComponents from '@/components/_common/SchemaForm/MediaSupportComponents';
import MediaBox from '@/components/_common/SchemaForm/MediaBox';
import AudioBox from '@/components/_common/SchemaForm/AudioBox';
import SubtitleBox from '@/components/_common/SchemaForm/SubtitleBox';

interface SchemaDisplayProps {
  schema: RJSFSchema;
  inputs: any;
  definitions?: any;
  rowSpacing?: number;
  columnSpacing?: number;
}

const ORDER_SPEC_FORMAT = ['MediaResolutionPicker', 'MultiLanguagePicker'];

const SchemaDisplay = ({ schema = {}, inputs, definitions, rowSpacing, columnSpacing }: SchemaDisplayProps) => {
  const versionKeys = useAppSelector(selectVersionKeys);
  const versionOptionLabel = useContext(ConfigContext)?.orderPlacementConfig?.versionOptionLabel;
  if (!schema) return null;
  const outputVersionKey =
    (schema.properties && Object.keys(schema.properties).find((key) => (schema.properties?.[key] as any)?.format === 'VersionOutput')) || '';
  const programOutputVersionKey =
    (schema.properties && Object.keys(schema.properties).find((key) => (schema.properties?.[key] as any)?.format === 'ProgramVersionOutput')) || '';
  const ref = definitions && schema.items ? (schema.items as any).$ref : null;
  const defs = ref && definitions[ref?.replace('#/definitions/', '')];

  const renderFormatInputValue = (key: string, value: string | string[] | null, title: string, index: number) => {
    switch (key) {
      case 'MediaResolutionPicker':
        return <MediaBox key={`${key}-${index}`} value={value as string} />;
      case 'MultiLanguagePicker':
        if (title.toLowerCase().includes('audio')) return <AudioBox key={`${key}-${index}`} value={value as string[]} />;
        return <SubtitleBox key={`${key}-${index}`} value={value as string[]} />;
      default:
        return null;
    }
  };

  // OLD WAY -- HAVE TO KEEP BC CLIENTS
  const renderTitleInputValue = (key: string) => {
    if (key === outputVersionKey || key === programOutputVersionKey) {
      const version = versionKeys[inputs[key]];
      const program = versionKeys[version?.program_id];
      return (
        <>
          {versionOptionLabel && <Typography>{versionOptionLabel(versionKeys[inputs[key]])}</Typography>}
          <Typography variant="caption">{getLineageMetaTitle(program)}</Typography>
        </>
      );
    }

    if (key === 'media_support_components') {
      return <MediaSupportComponents data={inputs?.[key]} />;
    }

    switch ((schema.properties?.[key] as any)?.type) {
      case 'bool':
      case 'boolean':
        return <BoolIcon label={!inputs?.[key] ? 'No' : 'Yes'} checked={inputs?.[key]} />;
      case 'array':
        // if we are expecting an array but we get a string, don't crash
        if (typeof inputs?.[key] === 'string') return <Typography>{inputs?.[key]}</Typography>;
        return renderArraySchema(inputs?.[key]);
      default:
        return <Typography>{typeof inputs?.[key] === 'string' ? inputs?.[key] : '-'}</Typography>;
    }
  };

  const renderInputValue = (schema: any, value: any) => {
    const valueIsObject = value && value.constructor === Object;
    if (valueIsObject) {
      const ref = definitions && schema.allOf ? schema.allOf?.[0]?.$ref : null;
      const defs = ref && definitions[ref?.replace('#/definitions/', '')];
      return <SchemaDisplay schema={defs} inputs={value} />;
    }
    switch (schema.type) {
      case 'string':
        return value;
      default:
        return value;
    }
  };

  return (
    <Grid container columnSpacing={columnSpacing} rowSpacing={rowSpacing}>
      {schema.type === 'array' ? (
        <Box
          sx={(theme) => ({
            p: 2,
            border: `1px solid ${theme.palette.default.main}5a`,
            pb: 0,
            borderRadius: 1,
            width: '100%'
          })}
        >
          <Typography variant="h4" color="primary" sx={{ fontSize: '1.1rem', p: 0, fontWeight: 'normal', mb: 2 }}>
            {schema.title}
          </Typography>
          {!inputs?.length ? (
            <Box sx={{ pb: 2 }}>-</Box>
          ) : (
            Array.isArray(inputs) &&
            inputs?.map((input: any, index: number) => (
              <Grid key={index} item xs={12} sx={{ pb: 2 }}>
                <Typography variant="h4" sx={{ fontSize: '1rem', p: 0, fontWeight: 'bold', mb: 2 }}>
                  {schema.title} {index + 1}
                </Typography>
                <SchemaDisplay schema={defs || (schema.items as RJSFSchema)} inputs={input} />
                {index !== inputs.length - 1 && (
                  <div>
                    <Divider sx={{ height: 1, mb: 2, backgroundColor: 'background.default' }} />
                  </div>
                )}
              </Grid>
            ))
          )}
        </Box>
      ) : schema.properties ? (
        Object.keys(schema.properties).map((key, index) => {
          if (!schema.properties?.[key]) return null;
          if (schema.properties[key]?.format?.length > 0 && ORDER_SPEC_FORMAT.includes(schema.properties[key].format)) {
            return renderFormatInputValue(schema.properties[key].format, inputs?.[key], key, index);
          }
          const defTitle = (schema.properties[key] as any)?.allOf?.[0]?.$ref
            ? schema.$defs?.[(schema.properties[key] as any)?.allOf?.[0]?.$ref.replace('#/$defs/', '')]?.title
            : null;
          const label =
            (schema.properties[key] as any)?.title || (schema.properties[key] as any)?.label || defTitle || titleCase(convertFromSnakeCase(key));
          const cols = (schema.properties[key] as any).ui_cols;

          return (
            <Grid key={key} item {...cols}>
              <LabelValue color="primary" key={key} label={label} sx={{ mr: 1, mb: 2 }}>
                {renderTitleInputValue(key)}
              </LabelValue>
            </Grid>
          );
        })
      ) : (
        <LabelValue color="primary" label={schema.title} sx={{ mr: 1 }}>
          {renderInputValue(schema, inputs)}
        </LabelValue>
      )}
    </Grid>
  );
};

export default SchemaDisplay;

const renderArraySchema = (inputs: any[] | string) => {
  if (!inputs) return '-';
  if (!Array.isArray(inputs) && typeof inputs !== 'object') return inputs;
  if (!isPlainObject(inputs) && inputs.every((input) => typeof input === 'string')) return <Typography>{inputs.join(', ')} </Typography>;
  if (inputs.every((input) => isPlainObject(input))) {
    return (
      <>
        {inputs.map((input, key) => (
          <MultiObjectSchemaRender key={key}>
            {Object.entries(input).map(([key, value]) => (
              <span key={key}>{`${key}: ${renderArraySchema(value)}`}</span>
            ))}
          </MultiObjectSchemaRender>
        ))}
      </>
    );
  }
  return 'Error Displaying Value(s)';
};

function isPlainObject(variable: any) {
  return typeof variable === 'object' && variable !== null && !Array.isArray(variable);
}

const MultiObjectSchemaRender = styled(Typography)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  > * {
    min-width: 200px;
  }
`;
